<template>
  <div class="landing">
    <header class="landing-nav">
      <div class="landing-container landing-nav-inner">
        <router-link to="/" class="landing-logo">
          <img class="landing-logo-img" src="../assets/bea-payday-logo.png" alt="BeaPayDay Total Payroll System" />
        </router-link>
        <nav id="landing-mobile-nav" class="landing-nav-links" :class="{ 'is-open': mobileMenuOpen }">
          <a href="#about-hris" @click.prevent="scrollTo('about-hris')">About</a>
          <a href="#core-features" @click.prevent="scrollTo('core-features')">Core Features</a>
          <a href="#features" @click.prevent="scrollTo('features')">Features</a>
          <router-link to="/careers">Careers</router-link>

          <a href="#industries" @click.prevent="scrollTo('industries')">Industries</a>
          <a href="#compliance" @click.prevent="scrollTo('compliance')">Compliance</a>

          <a href="#how-it-works" @click.prevent="scrollTo('how-it-works')">How it works</a>
          <a href="#faq" @click.prevent="scrollTo('faq')">FAQ</a>
        </nav>
        <button type="button" class="landing-nav-toggle" :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
          aria-controls="landing-mobile-nav" @click="mobileMenuOpen = !mobileMenuOpen">
          {{ mobileMenuOpen ? "Close" : "Menu" }}
        </button>
        <div class="landing-nav-actions">
          <router-link to="/login" class="hris-btn hris-btn-ghost">Log in</router-link>
          <router-link to="/login" class="hris-btn hris-btn-primary">Get started</router-link>
        </div>
      </div>
    </header>

    <section class="landing-hero">
      <div class="landing-container landing-hero-grid">
        <div class="landing-hero-copy">
          <p class="landing-eyebrow">Philippine HR &amp; Payroll Platform</p>
          <h1>
            Payroll made
            <span class="landing-text-accent">effortless</span> from attendance
            to
            <span class="landing-text-accent">payslips</span>
          </h1>
          <p class="landing-lead">
            BeaPayDay brings recruitment, employee records, timekeeping, leave
            management, and Philippine-compliant payroll into one unified
            platform so your team can focus on people, not spreadsheets.
          </p>
          <div class="landing-hero-cta">
            <router-link to="/login" class="hris-btn hris-btn-primary">Get started free</router-link>
            <router-link to="/careers" class="hris-btn hris-btn-outline">Browse openings</router-link>
            <a href="#core-features" class="hris-btn hris-btn-outline"
              @click.prevent="scrollTo('core-features')">Explore features</a>
          </div>
          <!-- <p class="landing-trust-line">
            Built for Philippine businesses — SSS, PhilHealth, Pag-IBIG, and BIR
            ready.
          </p> -->
        </div>
        <div class="landing-hero-visual">
          <div class="mock-app">
            <div class="mock-app-bar">
              <span class="mock-app-brand">BeaPayDay</span>
              <span class="mock-app-bar-meta">Dashboard</span>
            </div>
            <div class="mock-app-body">
              <aside class="mock-sidebar">
                <div class="mock-nav-item active">Dashboard</div>
                <div class="mock-nav-item">Employees</div>
                <div class="mock-nav-item">Timekeeping</div>
                <div class="mock-nav-item">Payroll</div>
                <div class="mock-nav-item">Leaves</div>
              </aside>
              <main class="mock-main">
                <div class="mock-stats">
                  <div class="mock-stat">
                    <span class="mock-stat-num">128</span><span>Active staff</span>
                  </div>
                  <div class="mock-stat">
                    <span class="mock-stat-num">5</span><span>Pending leaves</span>
                  </div>
                  <div class="mock-stat">
                    <span class="mock-stat-num">98%</span><span>Payroll done</span>
                  </div>
                </div>
                <div class="mock-table">
                  <div class="mock-table-head">
                    <span>Employee</span><span>Department</span><span>Status</span>
                  </div>
                  <div class="mock-table-row">
                    <span>Maria Santos</span><span>Operations</span><span class="mock-pill">Active</span>
                  </div>
                  <div class="mock-table-row">
                    <span>Nicole Mendoza</span><span>Finance</span><span class="mock-pill">On leave</span>
                  </div>
                  <div class="mock-table-row">
                    <span>Ana Reyes</span><span>HR</span><span class="mock-pill">Active</span>
                  </div>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-logos">
      <div class="landing-container">
        <p class="landing-logos-label">Trusted compliance frameworks</p>
        <div class="gov-logos-grid">
          <div v-for="item in govLogos" :key="item.name" class="gov-logo-item">
            <div class="gov-logo-img-wrap">
              <img class="gov-logo-img" :src="item.logo" :alt="item.name" />
            </div>
            <div class="gov-logo-text">
              <span class="gov-logo-name">{{ item.name }}</span>
              <span class="gov-logo-desc">{{ item.description }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="about-hris" class="landing-section landing-intro-section landing-reveal">
      <div class="landing-container landing-intro-inner">
        <p class="landing-eyebrow">BeaPayDay HRIS</p>
        <h2 class="landing-intro-title">
          Welcome to the Future of
          <span class="landing-text-accent">Human Resource Management</span>
        </h2>
        <div class="landing-intro-copy">
          <p>
            Managing your workforce should be simple, efficient, and accessible
            anytime, anywhere. Our Human Resource Information System (HRIS) by
            BeaPayDay is a comprehensive cloud-based platform designed to
            streamline HR operations, automate repetitive tasks, and empower
            both HR professionals and employees through a secure web and mobile
            application.
          </p>
          <p>
            Whether you are a small business with 20 employees or a large
            enterprise with thousands of personnel across multiple branches, our
            HRIS provides the tools you need to manage your workforce
            efficiently while improving productivity, compliance, and employee
            engagement.
          </p>
        </div>
      </div>
    </section>

    <section class="landing-section landing-section-alt landing-reveal">
      <div class="landing-container landing-split">
        <div class="landing-split-copy">
          <h2>Everything You Need to Manage Your Workforce</h2>
          <p>
            Our HRIS combines all essential HR functions into a single
            integrated platform, eliminating manual paperwork, spreadsheets, and
            disconnected systems. From recruitment and onboarding to payroll,
            attendance, performance management, and employee self-service, every
            process is connected in one centralized system.
          </p>
          <p>
            With real-time reporting, automated workflows, and mobile
            accessibility, organizations can make faster, smarter decisions
            while reducing administrative costs and improving operational
            efficiency.
          </p>
        </div>
        <div class="landing-split-visual">
          <div class="feature-panel platform-highlights">
            <div class="feature-panel-header">Integrated HR Platform</div>
            <ul class="platform-highlight-list">
              <li v-for="(item, i) in platformHighlights" :key="item"
                class="platform-highlight-item reveal-stagger-item" :style="{ transitionDelay: i * 0.07 + 's' }">
                <span class="platform-check" aria-hidden="true">✓</span>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section id="core-features" class="landing-section core-features-section landing-reveal">
      <div class="landing-container">
        <div class="landing-section-head">
          <h2>Core Features</h2>
          <p>
            A complete suite of BeaPayDay capabilities — from employee records
            to intelligent automation — in one secure cloud platform.
          </p>
        </div>
        <div class="core-features-grid">
          <article v-for="(feature, i) in coreFeatures" :key="feature.title"
            class="core-feature-card reveal-stagger-item" :style="{ transitionDelay: i * 0.05 + 's' }">
            <span class="core-feature-index">{{ formatIndex(i) }}</span>
            <div class="core-feature-body">
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section id="why-choose" class="landing-section landing-section-alt landing-reveal">
      <div class="landing-container">
        <div class="landing-section-head">
          <h2>Why Choose BeaPayDay?</h2>
          <p>
            Built for modern organizations that need speed, compliance, and
            clarity across every HR workflow.
          </p>
        </div>
        <div class="why-choose-grid">
          <div v-for="(item, i) in whyChoose" :key="item" class="why-choose-item reveal-stagger-item"
            :style="{ transitionDelay: i * 0.06 + 's' }">
            <span class="why-choose-num">{{ i + 1 }}</span>
            <p>{{ item }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="industries" class="landing-section landing-reveal">
      <div class="landing-container">
        <div class="landing-section-head">
          <h2>Industries We Serve</h2>
          <p>
            Our HRIS is designed for organizations across a wide range of
            industries — from corporate offices to government agencies.
          </p>
        </div>
        <div class="industries-grid">
          <span v-for="(industry, i) in industries" :key="industry" class="industry-pill reveal-stagger-item"
            :style="{ transitionDelay: i * 0.04 + 's' }">{{ industry }}</span>
        </div>
      </div>
    </section>

    <section id="features" class="landing-section landing-reveal">
      <div class="landing-container landing-split">
        <div class="landing-split-copy">
          <h2>Employee management from hire to retire</h2>
          <p>
            Maintain complete 201 files, track career updates, manage schedules,
            and handle exit interviews — all in one place. Role-based access
            ensures the right people see the right data across departments and
            branches.
          </p>
          <ul class="landing-list">
            <li>Employee directory with active, hold, and inactive status</li>
            <li>Module permissions and role-based access control</li>
            <li>Endorsement workflow from recruitment to onboarding</li>
            <li>Incident reports, NTE, and sanctions tracking</li>
          </ul>
        </div>
        <div class="landing-split-visual">
          <div class="feature-panel">
            <div class="feature-panel-header">Employee Directory</div>
            <div class="feature-panel-row">
              <span>ID</span><span>Name</span><span>Status</span>
            </div>
            <div class="feature-panel-row">
              <span>1024</span><span>Carlos Mendoza</span><span>Active</span>
            </div>
            <div class="feature-panel-row">
              <span>1025</span><span>Liza Fernandez</span><span>Active</span>
            </div>
            <div class="feature-panel-row">
              <span>1026</span><span>Mark Tolentino</span><span>On hold</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section landing-section-alt">
      <div class="landing-container landing-split landing-split-reverse">
        <div class="landing-split-copy">
          <h2>Timekeeping that feeds payroll automatically</h2>
          <p>
            Upload schedules and timelogs, generate DTRs, and track attendance
            in real time. Detailed timekeeping rolls up into payroll summaries —
            no manual re-entry.
          </p>
          <ul class="landing-list">
            <li>Check-in and check-out with break and lunch tracking</li>
            <li>Monthly, hourly, and binary schedule formats</li>
            <li>Holiday calendar and pay period configuration</li>
            <li>Optional geofencing and NFC attendance support</li>
          </ul>
        </div>
        <div class="landing-split-visual">
          <div class="feature-panel">
            <div class="feature-panel-header">Today's Attendance</div>
            <div class="attendance-grid">
              <div class="attendance-cell">
                <span class="attendance-count">42</span><span>Clocked in</span>
              </div>
              <div class="attendance-cell">
                <span class="attendance-count">8</span><span>On break</span>
              </div>
              <div class="attendance-cell">
                <span class="attendance-count">12</span><span>Not yet in</span>
              </div>
              <div class="attendance-cell">
                <span class="attendance-count">66</span><span>Clocked out</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section landing-section-alt">
      <div class="landing-container landing-split">
        <div class="landing-split-copy">
          <h2>Philippine payroll, done right</h2>
          <p>
            Process payroll with built-in SSS, PhilHealth, Pag-IBIG, and
            withholding tax tables. Generate payslips, 13th month pay,
            government contribution reports, and BIR alphalist from the same
            system that tracks your timekeeping.
          </p>
          <ul class="landing-list">
            <li>Automated statutory deductions and tax computation</li>
            <li>Adjustments, loans, and cash bond management</li>
            <li>Detailed payslip and payroll history</li>
            <li>Employee self-service payslip portal</li>
          </ul>
        </div>
        <div class="landing-split-visual">
          <div class="feature-panel">
            <div class="feature-panel-header">
              Payroll Summary — January 2026
            </div>
            <div class="payroll-summary">
              <div class="payroll-total">
                <span class="payroll-label">Total payroll</span>
                <span class="payroll-amount">Processed</span>
              </div>
              <div class="payroll-bar">
                <div class="payroll-bar-fill" style="width: 76%"></div>
              </div>
              <div class="payroll-meta">
                <span>98 of 128 employees paid</span><span>76% complete</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="compliance" class="landing-section landing-reveal">
      <div class="landing-container">
        <div class="landing-section-head">
          <h2>Government compliance built in</h2>
          <p>
            No add-ons required — Philippine statutory requirements are part of
            the core platform.
          </p>
        </div>
        <div class="compliance-grid">
          <div v-for="item in compliance" :key="item.title" class="compliance-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="how-it-works" class="landing-section landing-section-alt how-it-works-section landing-reveal">
      <div class="landing-container">
        <div class="landing-section-head">
          <h2>How BeaPayDay works</h2>
          <p>From recruitment to retirement — one connected workflow.</p>
        </div>
        <div class="steps-grid steps-grid-centered">
          <div v-for="(step, i) in steps" :key="step.title" class="step-card"
            :class="{ 'step-card-last': i === steps.length - 1 }">
            <span class="step-num">{{ i + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section landing-reveal">
      <div class="landing-container landing-split landing-split-reverse">
        <div class="landing-split-copy">
          <h2>Leave and forms, simplified</h2>
          <p>
            Employees submit leave applications, change-of-schedule requests,
            overtime, undertime, WFH, and official business trip forms online.
            Approvers review and act from the same dashboard — with full audit
            trails and reporting.
          </p>
        </div>
        <div class="landing-split-visual">
          <div class="feature-panel">
            <div class="feature-panel-header">Recent Leave Requests</div>
            <div class="feature-panel-row">
              <span>Maria S.</span><span>Vacation</span><span class="mock-pill">Pending</span>
            </div>
            <div class="feature-panel-row">
              <span>Juan D.</span><span>Sick</span><span class="mock-pill approved">Approved</span>
            </div>
            <div class="feature-panel-row">
              <span>Ana R.</span><span>Emergency</span><span class="mock-pill">Pending</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="download" class="landing-section landing-reveal">
      <div class="landing-container app-badges-inner">
        <div class="landing-section-head">
          <h2>Take BeaPayDay on the go</h2>
          <p>
            Employees can clock in, request leaves, and view payslips from the
            mobile app.
          </p>
        </div>
        <div class="app-badges-row">
          <a href="https://apps.apple.com/il/app/beapayday/id6759442549" class="app-badge-link" target="_blank"
            rel="noopener noreferrer" aria-label="Download BeaPayDay on the App Store">
            <svg class="app-badge-icon app-badge-icon-apple" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
                fill="currentColor" />
            </svg>
            <span class="app-badge-text">
              <span class="app-badge-label">Download on the</span>
              <span class="app-badge-store">App Store</span>
            </span>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.je310Solution.hrisbeapay"
            class="app-badge-link app-badge-link-play" target="_blank" rel="noopener noreferrer"
            aria-label="Get BeaPayDay on Google Play">
            <svg class="app-badge-icon app-badge-icon-play" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335"
                d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5Z" />
              <path fill="#FBBC04" d="M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12Z" />
              <path fill="#4285F4"
                d="M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81Z" />
              <path fill="#34A853" d="M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
            </svg>
            <span class="app-badge-text">
              <span class="app-badge-label">Get it on</span>
              <span class="app-badge-store">Google Play</span>
            </span>
          </a>
        </div>
      </div>
    </section>

    <section id="faq" class="landing-section landing-section-alt landing-reveal">
      <div class="landing-container">
        <div class="landing-section-head">
          <h2>Frequently asked questions</h2>
          <p>
            Quick answers about BeaPayDay and Philippine payroll compliance.
          </p>
        </div>
        <div class="faq-list">
          <div v-for="(item, i) in faqs" :key="item.question" class="faq-item" :class="{ 'is-open': openFaq === i }">
            <button type="button" class="faq-question" :aria-expanded="openFaq === i ? 'true' : 'false'"
              @click="toggleFaq(i)">
              <span>{{ item.question }}</span>
              <span class="faq-toggle">{{ openFaq === i ? "−" : "+" }}</span>
            </button>
            <div v-show="openFaq === i" class="faq-answer">
              <p>{{ item.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-cta landing-reveal">
      <div class="landing-container landing-cta-inner">
        <h2>Transform Your HR Operations Today</h2>
        <p class="landing-cta-lead">
          Empower your HR team, simplify workforce management, and create a
          better employee experience with a modern Human Resource Information
          System. From recruitment to retirement, our HRIS helps you automate
          processes, improve productivity, and make informed decisions through
          real-time data and intelligent insights.
        </p>
        <p class="landing-cta-tagline">
          Your people are your greatest asset. Let our HRIS help you manage them
          with confidence, efficiency, and innovation.
        </p>
        <div class="landing-footer-cta">
          <router-link to="/login" class="hris-btn hris-btn-primary hris-btn-lg">Get started today</router-link>
          <router-link to="/careers" class="hris-btn hris-btn-primary hris-btn-lg">Browse openings</router-link>
        </div>
      </div>
    </section>

    <footer class="landing-footer">
      <div class="landing-container landing-footer-grid">
        <div class="footer-brand">
          <a href="https://je310solution.com/" target="_blank" rel="noopener noreferrer"
            class="landing-logo landing-logo-footer">
            <img class="landing-logo-img landing-logo-img-footer" src="../assets/beapayday-powered-by-footer.png"
              alt="BeaPayDay Total Payroll System powered by JE310 Solution Inc." />
          </a>
          <p>BeaPayDay — Philippine HR &amp; Payroll Platform</p>
          <p class="footer-address">
            3A PG Building, West Avenue, Barangay Paltok, Quezon City,
            Philippines
          </p>
        </div>
        <div class="footer-col">
          <h4>Product</h4>
          <a href="#about-hris" @click.prevent="scrollTo('about-hris')">About</a>
          <a href="#core-features" @click.prevent="scrollTo('core-features')">Core Features</a>
          <a href="#features" @click.prevent="scrollTo('features')">Features</a>
          <a href="#compliance" @click.prevent="scrollTo('compliance')">Compliance</a>
          <a href="#industries" @click.prevent="scrollTo('industries')">Industries</a>
          <a href="#faq" @click.prevent="scrollTo('faq')">FAQ</a>
        </div>
        <div class="footer-col">
          <h4>Company</h4>
          <a href="https://je310solution.com/" target="_blank" rel="noopener noreferrer">JE 310 Solution</a>
          <router-link to="/login">Sign in</router-link>
          <router-link to="/careers">Browse openings</router-link>
          <router-link to="/employment-application">Apply for a job</router-link>
          <router-link to="/privacy-policy">Privacy policy</router-link>
        </div>
        <div class="footer-col">
          <h4>Contact</h4>
          <a href="tel:+639524697039" class="footer-contact-item">
            <svg class="footer-contact-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6.6 10.8a15.1 15.1 0 006.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1l-2.2 2.2z"
                stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>+63 952 469 7039</span>
          </a>
          <a href="mailto:info@beapayday.com" class="footer-contact-item">
            <svg class="footer-contact-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 6.5h16c.6 0 1 .4 1 1v11c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1v-11c0-.6.4-1 1-1z"
                stroke="currentColor" stroke-width="1.75" stroke-linejoin="round" />
              <path d="m4 7.5 8 5.5 8-5.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <span>info@beapayday.com</span>
          </a>
        </div>
      </div>
      <div class="landing-container landing-footer-bottom">
        <span>&copy; {{ currentYear }} BeaPayDay. All rights reserved.</span>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "Landing",
  data() {
    return {
      currentYear: new Date().getFullYear(),
      mobileMenuOpen: false,
      openFaq: null,
      revealObserver: null,
      govLogos: [
        {
          name: "SSS",
          description: "Social Security",
          logo: "https://www.timekeep.ph/logos/sss-logo.svg?dpl=dpl_9jRr5jzkTqp2L9XuTyEriJnhoYGN",
        },
        {
          name: "PhilHealth",
          description: "Health Insurance",
          logo: "https://www.timekeep.ph/logos/philhealth-logo.svg?dpl=dpl_9jRr5jzkTqp2L9XuTyEriJnhoYGN",
        },
        {
          name: "Pag-IBIG",
          description: "Housing Fund",
          logo: "https://www.timekeep.ph/logos/pagibig-logo.svg?dpl=dpl_9jRr5jzkTqp2L9XuTyEriJnhoYGN",
        },
        {
          name: "BIR",
          description: "2316 Tax Certification",
          logo: require("../assets/compliance-logos/bir-logo.png"),
        },
      ],
      compliance: [
        {
          title: "SSS Contributions",
          description:
            "Automated SSS employee and employer share computation with contribution tables and reports.",
        },
        {
          title: "PhilHealth",
          description:
            "PhilHealth premium calculations integrated into every payroll run.",
        },
        {
          title: "Pag-IBIG",
          description:
            "Pag-IBIG fund deductions with configurable rates and contribution reporting.",
        },
        {
          title: "BIR Withholding Tax",
          description:
            "Withholding tax tables, tax status management, and Form 2316 generation.",
        },
        {
          title: "Alphalist",
          description:
            "Annual alphalist preparation for BIR filing requirements.",
        },
        {
          title: "13th Month Pay",
          description:
            "Dedicated 13th month pay processing aligned with Philippine labor law.",
        },
      ],
      steps: [
        {
          title: "Recruit & onboard",
          description:
            "Manage applicants through exams and interviews, then endorse hired candidates directly into employee records.",
        },
        {
          title: "Track time",
          description:
            "Employees clock in and out; schedules and timelogs feed into detailed timekeeping summaries.",
        },
        {
          title: "Manage leaves & forms",
          description:
            "Leave and attendance-related requests flow through configurable approval chains.",
        },
        {
          title: "Run payroll",
          description:
            "Process payroll with statutory deductions, generate payslips, and export government reports.",
        },
      ],
      faqs: [
        {
          question:
            "Is BeaPayDay compliant with Philippine government requirements?",
          answer:
            "Yes. BeaPayDay includes built-in SSS, PhilHealth, Pag-IBIG, and BIR withholding tax tables, plus alphalist and Form 2316 support — no separate compliance add-ons required.",
        },
        {
          question:
            "Can employees access payslips and request leaves on mobile?",
          answer:
            "Yes. The BeaPayDay mobile app lets employees clock in, submit leave and attendance forms, and view payslips. Download links are available on the login page and above.",
        },
        {
          question: "How does timekeeping connect to payroll?",
          answer:
            "Attendance, schedules, and DTR summaries roll up into payroll automatically. You configure pay periods and cut-offs once; detailed timekeeping feeds each payroll run without re-entry.",
        },
        {
          question: "Do you support multiple branches or role-based access?",
          answer:
            "Yes. Role-based permissions control who sees employee records, approvals, and payroll data across departments and locations.",
        },
      ],
      platformHighlights: [
        "Recruitment & onboarding",
        "Payroll & attendance",
        "Employee self-service",
        "Real-time reporting",
        "Mobile accessibility",
        "Automated workflows",
      ],
      coreFeatures: [
        {
          title: "Employee Information Management",
          description:
            "Complete digital 201 files, profiles, and career records in one centralized system.",
        },
        {
          title: "Attendance and Timekeeping",
          description:
            "Track schedules, DTRs, and clock data that flows directly into payroll.",
        },
        {
          title: "Leave Management",
          description:
            "Apply, approve, and monitor leave balances with full audit trails.",
        },
        {
          title: "Payroll Management",
          description:
            "End-to-end payroll processing with statutory deductions and payslips.",
        },
        {
          title: "Employee Self-Service Portal",
          description:
            "Employees request leaves, view payslips, and access HR services online.",
        },
        {
          title: "Manager Self-Service",
          description:
            "Team dashboards, approvals, and HR actions from a dedicated manager view.",
        },
        {
          title: "Document Management",
          description:
            "Store, organize, and retrieve HR documents securely in the cloud.",
        },
        {
          title: "Reports and Analytics",
          description:
            "Real-time dashboards and exportable reports for smarter decisions.",
        },
        {
          title: "Mobile Application",
          description:
            "Clock in, request leaves, and access HR on iOS and Android devices.",
        },
        {
          title: "Intelligent Automation",
          description:
            "Automated workflows and reminders that reduce repetitive manual work.",
        },
        {
          title: "Security and Compliance",
          description:
            "Role-based access, audit logs, and Philippine statutory compliance built in.",
        },
        {
          title: "Cloud-Based and Scalable",
          description:
            "Secure cloud infrastructure that scales from small teams to enterprises.",
        },
        {
          title: "Recruitment",
          description:
            "Applicant pool, exams, interviews, training pipeline, and hiring workflow with SMS notifications.",
        },
        {
          title: "Online Forms",
          description:
            "CWS, OBT, overtime, undertime, WFH, and half-day requests with approval chains.",
        },
        {
          title: "Government Reports",
          description:
            "SSS, PhilHealth, Pag-IBIG contributions, alphalist, Form 2316, and tax tables.",
        },
        {
          title: "Training & E-Learning",
          description:
            "E-learning lessons and course management for employee development.",
        },
        {
          title: "Announcements",
          description:
            "Company-wide announcements with read tracking for all employees.",
        },
        {
          title: "Employee onboarding",
          description:
            "Reduce onboarding time through digital workflows that ensure new employees complete all necessary requirements before their first day.",
        },
        {
          title: "Performance Management",
          description:
            "Measure employee performance through customizable evaluation tools.",
        },
        {
          title: "Asset Management",
          description:
            "Track company assets assigned to employees.",
        },
        {
          title: "Document Management",
          description:
            "Store and organize HR documents securely.",
        },
        {
          title: "Recruitment and Applicant Tracking",
          description:
            "Manage the complete hiring process from job posting to employee onboarding.",
        },
        {
          title: "Zoom meeting integration",
          description:
            "Seamlessly schedule and manage applicant interviews",
        },
        {
          title: "Geolocation",
          description:
            "Ensure employees clock in and out within the designated area"

        },
      ],
      whyChoose: [
        "Centralized employee management",
        "Automated HR processes",
        "Faster payroll processing",
        "Improved compliance",
        "Mobile accessibility",
        "Real-time analytics",
        "Secure cloud infrastructure",
        "Seamless integration with payroll, attendance devices, and third-party systems",
        "Scalable for businesses of all sizes",
      ],
      industries: [
        "Corporate Offices",
        "Manufacturing",
        "Logistics and Warehousing",
        "Retail and E-Commerce",
        "Healthcare and Hospitals",
        "Educational Institutions",
        "Business Process Outsourcing (BPO)",
        "Construction",
        "Hospitality",
        "Government Agencies",
        "Recruitment and Manpower Services",
      ],
    };
  },
  mounted() {
    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            this.revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    this.$el.querySelectorAll(".landing-reveal").forEach((el) => {
      this.revealObserver.observe(el);
    });
    this.onResize = () => {
      if (window.innerWidth >= 1100) {
        this.mobileMenuOpen = false;
      }
    };
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    if (this.revealObserver) {
      this.revealObserver.disconnect();
    }
    if (this.onResize) {
      window.removeEventListener("resize", this.onResize);
    }
  },
  methods: {
    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? null : index;
    },
    scrollTo(id) {
      this.mobileMenuOpen = false;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    formatIndex(i) {
      return String(i + 1).padStart(2, "0");
    },
  },
};
</script>

<style scoped>
.landing {
  font-family: var(--hris-font);
  color: var(--hris-text);
  background: var(--hris-surface);
  font-size: 1.0625rem;
  overflow-x: clip;
}

.landing-hero-grid>*,
.landing-split>*,
.landing-hero-visual,
.landing-split-visual {
  min-width: 0;
}

.landing .hris-btn {
  font-size: 1rem;
}

.landing-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 clamp(16px, 4vw, 24px);
}

.landing-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--hris-border-light);
}

.landing-nav-inner {
  position: relative;
  display: flex;
  align-items: center;
  height: 64px;
  gap: 24px;
}

.landing-logo {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.landing-logo:hover {
  opacity: 0.9;
}

.landing-logo-img {
  display: block;
  height: 48px;
  width: auto;
  max-width: 280px;
  object-fit: contain;
}

.landing-logo-footer .landing-logo-img,
.landing-logo-img-footer {
  height: auto;
  max-height: 120px;
  width: auto;
  max-width: 260px;
}

.landing-nav-links {
  display: none;
  gap: 28px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.landing-nav-links a {
  font-size: 0.9375rem;
  color: var(--hris-text-secondary);
  text-decoration: none;
  transition: color 0.2s;
}

.landing-nav-links a:hover {
  color: var(--hris-text);
}

.landing-nav-toggle {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding: 8px 14px;
  font-size: 0.9375rem;
  font-weight: 500;
  font-family: inherit;
  color: var(--hris-text);
  background: var(--hris-surface-muted);
  border: 1px solid var(--hris-border-light);
  border-radius: var(--hris-radius-sm);
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.landing-nav-toggle:hover {
  background: var(--hris-accent-soft);
  border-color: var(--hris-accent);
  color: var(--hris-accent);
}

.landing-nav-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.landing-hero {
  padding: 96px 0 112px;
  background: linear-gradient(180deg,
      rgba(47, 143, 98, 0.07) 0%,
      rgba(47, 143, 98, 0.02) 45%,
      #fff 100%);
}

.landing-hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 64px;
  align-items: center;
}

.landing-eyebrow {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--hris-accent);
  margin: 0 0 16px;
}

.landing-hero h1 {
  font-size: clamp(2.5rem, 6vw, 3.75rem);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.03em;
  margin: 0 0 20px;
}

.landing-text-accent {
  color: var(--hris-accent);
}

.landing-lead {
  font-size: 1.125rem;
  line-height: 1.65;
  color: var(--hris-text-secondary);
  margin: 0 0 28px;
  max-width: 580px;
}

.landing-hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.landing-trust-line {
  font-size: 0.875rem;
  color: var(--hris-text-muted);
  margin: 0;
}

.hris-btn-lg {
  padding: 14px 32px;
  font-size: 1.0625rem;
}

.mock-app {
  border: 1px solid var(--hris-border);
  border-radius: var(--hris-radius);
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(26, 31, 46, 0.12);
  background: var(--hris-surface);
  transform: perspective(1200px) rotateY(-4deg) rotateX(2deg);
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}

.mock-app:hover {
  transform: perspective(1200px) rotateY(-2deg) rotateX(1deg);
  box-shadow: 0 28px 60px rgba(47, 143, 98, 0.15);
}

.mock-app-brand {
  color: var(--hris-accent);
}

.mock-app-bar {
  display: flex;
  justify-content: space-between;
  padding: 18px 24px;
  background: var(--hris-surface-muted);
  border-bottom: 1px solid var(--hris-border-light);
  font-size: 1.0625rem;
  font-weight: 600;
}

.mock-app-bar-meta {
  color: var(--hris-text-muted);
  font-weight: 500;
}

.mock-app-body {
  display: flex;
  min-height: 400px;
}

.mock-sidebar {
  width: 200px;
  padding: 20px 0;
  background: var(--hris-bg);
  border-right: 1px solid var(--hris-border-light);
}

.mock-nav-item {
  padding: 12px 20px;
  font-size: 1.0625rem;
  color: var(--hris-text-secondary);
}

.mock-nav-item.active {
  background: var(--hris-accent-soft);
  color: var(--hris-accent);
  font-weight: 600;
}

.mock-main {
  flex: 1;
  padding: 24px;
}

.mock-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.mock-stat {
  background: var(--hris-surface-muted);
  border-radius: var(--hris-radius-sm);
  padding: 20px;
  text-align: center;
  font-size: 1rem;
  color: var(--hris-text-secondary);
}

.mock-stat-num {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: var(--hris-text);
  margin-bottom: 8px;
}

.mock-table-head,
.mock-table-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr 0.8fr;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--hris-border-light);
}

.mock-table-row {
  font-size: 1.125rem;
}

.mock-table-row span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.mock-table-head {
  font-weight: 600;
  color: var(--hris-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.875rem;
}

.mock-pill {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--hris-surface-muted);
  font-size: 1rem;
}

.mock-pill.approved {
  background: var(--hris-accent-soft);
  color: var(--hris-accent);
}

.landing-logos {
  padding: 72px 0;
  border-top: 1px solid var(--hris-border-light);
  border-bottom: 1px solid var(--hris-border-light);
  background: var(--hris-bg);
}

.landing-logos-label {
  text-align: center;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--hris-text-muted);
  margin: 0 0 48px;
}

.gov-logos-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  gap: 56px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

.gov-logo-item {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
  min-width: 0;
  padding: 0;
  min-height: auto;
}

.gov-logo-img-wrap {
  width: 88px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gov-logo-img {
  max-width: 88px;
  max-height: 72px;
  width: auto;
  height: auto;
  object-fit: contain;
  flex-shrink: 0;
}

.gov-logo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.gov-logo-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--hris-text);
  line-height: 1.2;
  white-space: nowrap;
}

.gov-logo-desc {
  font-size: 1rem;
  color: var(--hris-text-secondary);
  line-height: 1.2;
  white-space: nowrap;
}

@media (max-width: 900px) {
  .gov-logos-grid {
    justify-content: center;
    flex-wrap: wrap;
    gap: 24px 32px;
    max-width: 100%;
    padding: 0 8px;
  }

  .gov-logo-item {
    flex: 0 0 auto;
  }

  .gov-logo-name,
  .gov-logo-desc {
    white-space: normal;
  }
}

.landing-section {
  padding: 120px 0;
  scroll-margin-top: 72px;
}

.landing-section-alt {
  background: var(--hris-bg);
}

.landing-section-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 20px;
}

.landing-section-head h2 {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 12px;
  color: var(--hris-accent);
}

.landing-section-head p {
  color: var(--hris-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.landing-split {
  display: grid;
  grid-template-columns: 1fr;
  gap: 64px;
  align-items: center;
}

.landing-split-copy h2 {
  font-size: clamp(1.375rem, 3.5vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 16px;
  color: var(--hris-accent);
}

.landing-split-copy p {
  color: var(--hris-text-secondary);
  line-height: 1.65;
  margin: 0 0 20px;
}

.landing-list {
  margin: 0;
  padding-left: 20px;
  color: var(--hris-text-secondary);
  line-height: 1.8;
}

.feature-panel {
  border: 1px solid var(--hris-border);
  border-radius: var(--hris-radius);
  overflow: hidden;
  background: var(--hris-surface);
  box-shadow: var(--hris-shadow-md);
}

.feature-panel-header {
  padding: 22px 28px;
  font-weight: 600;
  font-size: 1.25rem;
  background: var(--hris-surface-muted);
  border-bottom: 1px solid var(--hris-border-light);
}

.feature-panel-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.8fr);
  gap: 16px;
  padding: 20px 28px;
  font-size: 1.1875rem;
  border-bottom: 1px solid var(--hris-border-light);
}

.feature-panel-row span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.feature-panel-row span:last-child {
  text-align: right;
}

.attendance-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: var(--hris-border-light);
}

.attendance-cell {
  background: var(--hris-surface);
  padding: 40px 28px;
  text-align: center;
}

.attendance-count {
  display: block;
  font-size: 2.75rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.attendance-cell span:last-child {
  font-size: 1.1875rem;
  color: var(--hris-text-secondary);
}

.payroll-summary {
  padding: 32px 28px;
}

.payroll-total {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.payroll-label {
  font-size: 1.1875rem;
  color: var(--hris-text-secondary);
}

.payroll-amount {
  font-weight: 600;
  font-size: 1.3125rem;
}

.payroll-bar {
  height: 10px;
  background: var(--hris-surface-muted);
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 16px;
}

.payroll-bar-fill {
  height: 100%;
  background: var(--hris-accent);
  border-radius: 999px;
}

.payroll-meta {
  display: flex;
  justify-content: space-between;
  font-size: 1.125rem;
  color: var(--hris-text-muted);
}

.compliance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.compliance-card {
  padding: 24px;
  border: 1px solid var(--hris-border-light);
  border-radius: var(--hris-radius);
  background: var(--hris-surface);
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;
}

.compliance-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--hris-shadow-md);
  border-color: rgba(47, 143, 98, 0.25);
}

.compliance-card h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 10px;
  color: var(--hris-accent);
}

.compliance-card p {
  font-size: 0.9375rem;
  color: var(--hris-text-secondary);
  line-height: 1.6;
  margin: 0;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
}

.steps-grid-centered {
  max-width: 1040px;
  margin: 0 auto;
  grid-template-columns: repeat(4, minmax(220px, 1fr));
  justify-content: center;
}

.how-it-works-section {
  scroll-margin-top: 72px;
}

.how-it-works-section .steps-grid-centered {
  position: relative;
}

@media (min-width: 1025px) {
  .how-it-works-section .steps-grid-centered::before {
    content: "";
    position: absolute;
    top: 42px;
    left: 12%;
    right: 12%;
    height: 2px;
    background: linear-gradient(90deg,
        transparent,
        var(--hris-border) 8%,
        var(--hris-border) 92%,
        transparent);
    z-index: 0;
  }
}

.how-it-works-section .step-card {
  text-align: center;
  position: relative;
  z-index: 1;
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;
}

.how-it-works-section .step-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--hris-shadow-md);
  border-color: rgba(47, 143, 98, 0.25);
}

.step-card {
  padding: 28px 24px;
  border: 1px solid var(--hris-border-light);
  border-radius: var(--hris-radius);
  background: var(--hris-surface);
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: var(--hris-accent-soft);
  border: 2px solid rgba(47, 143, 98, 0.2);
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--hris-accent);
  text-align: center;
}

.step-card h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 10px;
  color: var(--hris-accent);
}

.step-card p {
  font-size: 0.9375rem;
  color: var(--hris-text-secondary);
  line-height: 1.6;
  margin: 0;
}

.landing-cta {
  padding: 120px 0;
  background: linear-gradient(135deg, #2f8f62 0%, #267a52 100%);
  color: #fff;
}

.landing-cta-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.landing-cta h2 {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  margin: 0 0 12px;
  max-width: 720px;
}

.landing-cta p {
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 28px;
}

.landing-cta .hris-btn-primary {
  background: #fff;
  color: var(--hris-accent);
}

.landing-cta .hris-btn-primary:hover {
  background: var(--hris-surface-muted);
  color: var(--hris-accent-hover);
}

.landing-reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.landing-reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.reveal-stagger-item {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.55s ease, transform 0.55s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;
}

.landing-reveal.is-visible .reveal-stagger-item {
  opacity: 1;
  transform: translateY(0);
}

.landing-intro-section {
  padding: 100px 0 80px;
  background: linear-gradient(180deg,
      rgba(47, 143, 98, 0.04) 0%,
      var(--hris-surface) 100%);
  position: relative;
  overflow: hidden;
}

.landing-intro-section::before {
  content: "";
  position: absolute;
  top: -120px;
  right: -80px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle,
      rgba(47, 143, 98, 0.08) 0%,
      transparent 70%);
  pointer-events: none;
}

.landing-intro-inner {
  position: relative;
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
}

.landing-intro-title {
  font-size: clamp(1.875rem, 4vw, 2.75rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin: 0 0 32px;
  color: var(--hris-text);
}

.landing-intro-copy {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.landing-intro-copy p {
  font-size: 1.125rem;
  line-height: 1.75;
  color: var(--hris-text-secondary);
  margin: 0;
}

.platform-highlights {
  box-shadow: var(--hris-shadow-md);
  transition: box-shadow 0.35s ease, transform 0.35s ease;
}

.platform-highlights:hover {
  box-shadow: 0 16px 48px rgba(47, 143, 98, 0.12);
  transform: translateY(-2px);
}

.platform-highlight-list {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}

.platform-highlight-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 28px;
  font-size: 1.0625rem;
  color: var(--hris-text-secondary);
  border-bottom: 1px solid var(--hris-border-light);
  transition: background 0.2s ease, color 0.2s ease;
}

.platform-highlight-item:last-child {
  border-bottom: none;
}

.platform-highlight-item:hover {
  background: var(--hris-accent-soft);
  color: var(--hris-text);
}

.platform-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--hris-accent-soft);
  color: var(--hris-accent);
  font-size: 0.875rem;
  font-weight: 700;
}

.core-features-section {
  background: linear-gradient(180deg,
      var(--hris-bg) 0%,
      var(--hris-surface) 50%,
      var(--hris-bg) 100%);
}

.core-features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.core-feature-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px 24px 24px;
  border: 1px solid var(--hris-border-light);
  border-radius: var(--hris-radius);
  background: var(--hris-surface);
  overflow: hidden;
  transition: transform 0.35s ease, box-shadow 0.35s ease,
    border-color 0.35s ease;
}

.core-feature-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--hris-accent), #267a52);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.35s ease;
}

.core-feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(47, 143, 98, 0.12);
  border-color: rgba(47, 143, 98, 0.28);
}

.core-feature-card:hover::before {
  transform: scaleX(1);
}

.core-feature-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--hris-accent);
  background: var(--hris-accent-soft);
  border: 1px solid rgba(47, 143, 98, 0.15);
  border-radius: var(--hris-radius-sm);
}

.core-feature-body h3 {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0 0 10px;
  color: var(--hris-accent);
  transition: color 0.2s ease;
}

.core-feature-body p {
  font-size: 0.9375rem;
  line-height: 1.65;
  color: var(--hris-text-secondary);
  margin: 0;
}

.core-feature-card:hover .core-feature-body h3 {
  color: var(--hris-accent-hover);
}

@media (max-width: 1024px) {
  .core-features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 560px) {
  .core-features-grid {
    grid-template-columns: 1fr;
  }
}

.why-choose-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  max-width: 1100px;
  margin: 0 auto;
}

.why-choose-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px;
  border: 1px solid var(--hris-border-light);
  border-radius: var(--hris-radius);
  background: var(--hris-surface);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.why-choose-item:hover {
  transform: translateY(-3px);
  box-shadow: var(--hris-shadow-md);
  border-color: rgba(47, 143, 98, 0.25);
}

.why-choose-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #2f8f62 0%, #267a52 100%);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
}

.why-choose-item p {
  margin: 4px 0 0;
  font-size: 1rem;
  line-height: 1.55;
  color: var(--hris-text-secondary);
}

.industries-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  max-width: 960px;
  margin: 0 auto;
}

.industry-pill {
  display: inline-flex;
  align-items: center;
  padding: 12px 20px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--hris-text-secondary);
  background: var(--hris-surface);
  border: 1px solid var(--hris-border-light);
  border-radius: 999px;
  transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease,
    transform 0.25s ease, box-shadow 0.25s ease;
}

.industry-pill:hover {
  background: var(--hris-accent-soft);
  color: var(--hris-accent);
  border-color: rgba(47, 143, 98, 0.35);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(47, 143, 98, 0.1);
}

.landing-cta-lead {
  max-width: 720px;
  margin: 0 auto 16px;
  line-height: 1.65;
}

.landing-cta-tagline {
  font-size: 1.125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  max-width: 640px;
  margin: 0 auto 28px;
  line-height: 1.6;
}

.app-badges-inner {
  text-align: center;
}

.app-badges-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.app-badge-link {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 10px 18px 10px 16px;
  text-decoration: none;
  color: #fff;
  background: #000;
  border: 1.5px solid rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  transition: border-color 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.app-badge-link:hover {
  transform: translateY(-2px);
  border-color: #fff;
  opacity: 0.92;
}

.app-badge-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.app-badge-icon-apple {
  color: #fff;
}

.app-badge-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
  text-align: left;
}

.app-badge-label {
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.88);
}

.app-badge-store {
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #fff;
}

.app-badge-link-play {
  color: #000;
  background: #fff;
  border-color: rgba(0, 0, 0, 0.85);
}

.app-badge-link-play:hover {
  border-color: #000;
}

.app-badge-link-play .app-badge-label,
.app-badge-link-play .app-badge-store {
  color: #000;
}

.app-badge-link-play .app-badge-label {
  color: rgba(0, 0, 0, 0.72);
}

.faq-list {
  max-width: 720px;
  margin: 0 auto;
}

.faq-item {
  border: 1px solid var(--hris-border-light);
  border-radius: var(--hris-radius-sm);
  background: var(--hris-surface);
  margin-bottom: 12px;
  overflow: hidden;
  transition: border-color 0.2s;
}

.faq-item.is-open {
  border-color: rgba(47, 143, 98, 0.35);
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 18px 20px;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  text-align: left;
  color: var(--hris-text);
  background: transparent;
  border: none;
  cursor: pointer;
}

.faq-question:hover {
  color: var(--hris-accent);
}

.faq-toggle {
  flex-shrink: 0;
  font-size: 1.25rem;
  font-weight: 400;
  color: var(--hris-accent);
  line-height: 1;
}

.faq-answer {
  padding: 0 20px 18px;
}

.faq-answer p {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: var(--hris-text-secondary);
}

.landing-footer {
  padding: 80px 0 40px;
  background: var(--hris-surface);
  border-top: 1px solid var(--hris-border-light);
}

.landing-footer-grid {
  display: grid;
  grid-template-columns: 1.5fr repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 40px;
}

.footer-brand p {
  font-size: 0.9375rem;
  color: var(--hris-text-secondary);
  margin: 8px 0 0;
  line-height: 1.5;
}

.footer-address {
  font-size: 0.875rem !important;
  margin-top: 12px !important;
}

.footer-col h4 {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--hris-accent);
  margin: 0 0 14px;
}

.footer-col a,
.footer-col span {
  display: block;
  font-size: 0.9375rem;
  color: var(--hris-text-secondary);
  text-decoration: none;
  margin-bottom: 8px;
}

.footer-col a:hover {
  color: var(--hris-accent);
}

.footer-col a.footer-contact-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.footer-col a.footer-contact-item span {
  display: inline;
  margin-bottom: 0;
}

.footer-contact-icon {
  display: block;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: var(--hris-accent);
}

.footer-contact-item:hover .footer-contact-icon {
  color: var(--hris-accent-hover);
}

.landing-footer-bottom {
  padding-top: 24px;
  border-top: 1px solid var(--hris-border-light);
  font-size: 0.875rem;
  color: var(--hris-text-muted);
}

@media (max-width: 1024px) {
  .steps-grid-centered {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
    max-width: 560px;
  }

  .landing-footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 28px;
  }

  .landing-section {
    padding: 80px 0;
  }

  .landing-intro-section {
    padding: 72px 0 64px;
  }

  .landing-cta {
    padding: 80px 0;
  }
}

@media (max-width: 560px) {
  .steps-grid-centered {
    grid-template-columns: 1fr;
    max-width: 360px;
  }
}

@media (min-width: 992px) {
  .landing-hero-grid {
    grid-template-columns: 1fr 1fr;
  }

  .landing-split {
    grid-template-columns: 1fr 1fr;
  }

  .landing-split-reverse .landing-split-copy {
    order: 2;
  }

  .landing-split-reverse .landing-split-visual {
    order: 1;
  }
}

@media (min-width: 1100px) {
  .landing-nav-toggle {
    display: none;
  }

  .landing-nav-links {
    display: flex;
  }

  .landing-nav-actions {
    margin-left: auto;
  }
}

@media (min-width: 1280px) {
  .landing-container {
    padding: 0 24px;
  }
}

@media (max-width: 1099px) {
  .landing-nav-inner {
    flex-wrap: wrap;
    height: auto;
    min-height: 64px;
    padding: 12px 0;
    gap: 12px;
  }

  .landing-nav-links {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    padding: 8px clamp(16px, 4vw, 24px) 16px;
    background: rgba(255, 255, 255, 0.98);
    border-bottom: 1px solid var(--hris-border-light);
    box-shadow: var(--hris-shadow-md);
    transform: none;
    z-index: 10;
  }

  .landing-nav-links.is-open {
    display: flex;
  }

  .landing-nav-links a {
    padding: 12px 0;
    border-bottom: 1px solid var(--hris-border-light);
  }

  .landing-nav-links a:last-child {
    border-bottom: none;
  }

  .landing-nav-toggle {
    margin-left: auto;
  }

  .landing-nav-actions {
    margin-left: 0;
  }

  .landing-nav-actions .hris-btn-ghost {
    display: none;
  }

  .landing-hero-visual .mock-app {
    transform: none;
  }

  .landing-hero-visual .mock-app:hover {
    transform: none;
  }
}

@media (max-width: 991px) {
  .mock-sidebar {
    width: 160px;
  }

  .mock-nav-item {
    padding: 10px 14px;
    font-size: 0.9375rem;
  }

  .mock-main {
    padding: 16px;
  }

  .mock-app-body {
    min-height: 320px;
  }

  .attendance-count {
    font-size: 2.25rem;
  }

  .attendance-cell {
    padding: 28px 16px;
  }
}

@media (max-width: 767px) {
  .landing-hero {
    padding: 56px 0 72px;
  }

  .landing-section {
    padding: 64px 0;
    scroll-margin-top: 64px;
  }

  .landing-intro-section {
    padding: 64px 0 56px;
  }

  .landing-logos {
    padding: 48px 0;
  }

  .landing-logos-label {
    margin-bottom: 32px;
  }

  .landing-cta {
    padding: 64px 0;
  }

  .landing-footer {
    padding: 48px 0 32px;
  }

  .landing-section-head {
    margin-bottom: 40px;
  }

  .landing-hero-grid,
  .landing-split {
    gap: 40px;
  }

  .landing-lead {
    max-width: none;
  }

  .landing-hero-cta {
    flex-direction: column;
    align-items: stretch;
  }

  .landing-hero-cta .hris-btn {
    width: 100%;
  }

  .mock-app-body {
    flex-direction: column;
    min-height: auto;
  }

  .mock-sidebar {
    width: 100%;
    display: flex;
    overflow-x: auto;
    padding: 10px 12px;
    border-right: none;
    border-bottom: 1px solid var(--hris-border-light);
    -webkit-overflow-scrolling: touch;
  }

  .mock-nav-item {
    flex-shrink: 0;
    white-space: nowrap;
    padding: 8px 14px;
    border-radius: var(--hris-radius-sm);
  }

  .mock-stats {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mock-stat {
    padding: 16px;
  }

  .mock-stat-num {
    font-size: 1.75rem;
  }

  .mock-table-head,
  .mock-table-row {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 0.8fr);
    gap: 8px;
    padding: 12px 0;
  }

  .mock-table-row {
    font-size: 0.9375rem;
  }

  .feature-panel-header {
    padding: 16px 18px;
    font-size: 1.0625rem;
  }

  .feature-panel-row {
    padding: 14px 18px;
    font-size: 0.9375rem;
    gap: 10px;
  }

  .payroll-summary {
    padding: 24px 18px;
  }

  .payroll-meta {
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }

  .attendance-cell {
    padding: 24px 12px;
  }

  .attendance-count {
    font-size: 2rem;
  }

  .attendance-cell span:last-child {
    font-size: 0.9375rem;
  }

  .app-badges-row {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  .app-badge-link {
    padding: 8px 16px 8px 14px;
    gap: 10px;
  }

  .app-badge-icon {
    width: 26px;
    height: 26px;
  }

  .app-badge-label {
    font-size: 0.625rem;
  }

  .app-badge-store {
    font-size: 1rem;
  }

  .landing-footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  .footer-brand {
    grid-column: 1 / -1;
  }

  .why-choose-grid {
    grid-template-columns: 1fr;
  }

  .compliance-grid {
    grid-template-columns: 1fr;
  }

  .platform-highlight-item {
    padding: 14px 18px;
  }

  .core-feature-card {
    padding: 22px 18px 20px;
  }
}

@media (min-width: 992px) and (max-width: 1099px) {
  .mock-sidebar {
    width: 140px;
  }

  .mock-nav-item {
    padding: 10px 12px;
    font-size: 0.875rem;
  }

  .mock-main {
    padding: 16px;
  }

  .mock-stat {
    padding: 14px 10px;
    font-size: 0.875rem;
  }

  .mock-stat-num {
    font-size: 1.5rem;
  }

  .mock-table-row {
    font-size: 0.9375rem;
  }
}

@media (max-width: 479px) {
  .landing-nav .landing-logo-img {
    height: 36px;
    max-width: 180px;
  }

  .landing-logo-footer .landing-logo-img,
  .landing-logo-img-footer {
    max-height: 100px;
    max-width: 220px;
  }

  .landing-nav-actions .hris-btn {
    padding: 10px 14px;
    font-size: 0.875rem;
  }

  .landing-hero h1 {
    font-size: clamp(2rem, 9vw, 2.5rem);
  }

  .landing-footer-grid {
    grid-template-columns: 1fr;
  }

  .gov-logos-grid {
    gap: 20px;
  }

  .gov-logo-item {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }

  .gov-logo-img-wrap {
    width: 72px;
  }

  .gov-logo-img {
    max-width: 72px;
    max-height: 56px;
  }

  .industry-pill {
    width: 100%;
    justify-content: center;
    text-align: center;
  }

  .faq-question {
    padding: 14px 16px;
    font-size: 0.9375rem;
  }

  .faq-answer {
    padding: 0 16px 14px;
  }
}

.landing-footer-cta {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
</style>
